import { SortingListingType } from '@/api/algolia/types';
import { getUserLocation } from '@/api/mapbox';
import { getGeoLocationParams } from '@/app/_components/widgets/listing';
import { useListingFilters } from '@/app/_hooks/useListingFilters';
import { ElOrderItem } from '@davincihealthcare/elty-design-system-react';
import { CalendarDaysIcon, CurrencyEuroIcon, MapPinIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx/lite';
import { useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';

type ListingOrderProps = {
  loadingOrderValues: {
    closestDistance?: boolean;
    cheapestInEuros?: boolean;
    firstAvailability?: boolean;
  };
};

export const ListingOrder = ({ loadingOrderValues }: ListingOrderProps) => {
  const params = useSearchParams();
  const [isUserLocationLoading, setUserLocationLoading] = useState(false);

  const {
    filters: { order },
    applyFilter,
  } = useListingFilters();

  const selectOrReset = (input: SortingListingType, additionalParams?: URLSearchParams) => {
    if (order === input) {
      applyFilter('order', undefined);
    } else {
      applyFilter('order', input, additionalParams);
    }
  };

  const temporaryOrder = useMemo(() => (isUserLocationLoading ? SortingListingType.distanceAsc : order), [isUserLocationLoading, order]);

  const getPosition = async () => {
    setUserLocationLoading(true);
    const userLocation = await getUserLocation();
    if (!userLocation?.userCoordinates || !userLocation.userLocationName) {
      setUserLocationLoading(false);
      return;
    }
    const geoParams = getGeoLocationParams(params, {
      place: userLocation.userLocationName,
      coordinates: { lat: userLocation.userCoordinates[0], lng: userLocation.userCoordinates[1] },
    });
    selectOrReset(SortingListingType.distanceAsc, geoParams);
    setUserLocationLoading(false);
  };

  return (
    <div className="mb-4 flex max-h-[70px] flex-row items-center justify-between">
      <ElOrderItem
        selected={temporaryOrder === SortingListingType.distanceAsc}
        headline={<MapPinIcon className="size-4 md:size-5" />}
        label={'Dalla più vicina a te'}
        loading={isUserLocationLoading || loadingOrderValues.closestDistance}
        labelUnderlined={false}
        size="small"
        onClick={getPosition}
        className={clsx(
          'w-full justify-center rounded-l-lg',
          !(temporaryOrder === SortingListingType.distanceAsc) &&
            'text-balance text-neutral-darker hover:!bg-brandBlue-100 hover:text-primary-hover',
        )}
      />
      <div className="h-full w-[4px] bg-[#D1D7E2]" />
      <ElOrderItem
        selected={temporaryOrder === SortingListingType.priceAsc}
        headline={<CurrencyEuroIcon className="size-4 md:size-5" />}
        label={`Dalla più economica`}
        labelUnderlined={false}
        size="small"
        loading={loadingOrderValues.cheapestInEuros}
        onClick={() => selectOrReset(SortingListingType.priceAsc)}
        className={clsx(
          'w-full justify-center',
          !(temporaryOrder === SortingListingType.priceAsc) &&
            'text-balance text-neutral-darker hover:!bg-brandBlue-100 hover:text-primary-hover',
        )}
      />
      <div className="h-full w-[4px] bg-[#D1D7E2]" />
      <ElOrderItem
        selected={temporaryOrder === SortingListingType.firstAvailabilityAsc}
        headline={<CalendarDaysIcon className="size-4 md:size-5" />}
        label={`Dalla prima disponibile`}
        labelUnderlined={false}
        size="small"
        loading={loadingOrderValues.firstAvailability}
        onClick={() => selectOrReset(SortingListingType.firstAvailabilityAsc)}
        className={clsx(
          'w-full justify-center rounded-r-lg',
          !(temporaryOrder === SortingListingType.firstAvailabilityAsc) &&
            'text-balance text-neutral-darker hover:!bg-brandBlue-100 hover:text-primary-hover',
        )}
      />
    </div>
  );
};
